(() => {
  if (window.hsCartInteractions) return;
  window.hsCartInteractions = true;
  let busy = false;
  let needsSync = false;
  const controls = '[data-cart-add], [data-cart-change], [data-cart-remove-line], .checkout-button';
  const drawer = () => document.getElementById('cart-drawer');
  const root = () => window.Shopify?.routes?.root || '/';
  const state = (element) => JSON.parse(element.querySelector('[data-cart-state]').textContent);

  function feedback(key, error = false, retry = false, message = '') {
    const box = drawer()?.querySelector('[data-cart-feedback]');
    if (!box) return;
    box.classList.add('is-visible');
    box.classList.toggle('is-error', error);
    box.querySelector('[data-cart-message]').textContent = message || box.dataset[key];
    box.querySelector('[data-cart-retry]').hidden = !retry;
  }

  function lock(value) {
    const cart = drawer();
    if (!cart) return;
    cart.setAttribute('aria-busy', String(value));
    cart.querySelectorAll(controls).forEach(button => { button.disabled = value; });
  }

  async function readResponse(response) {
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : '');
    return data;
  }

  async function request(path, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      return await fetch(path, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } }).then(readResponse);
    } finally {
      clearTimeout(timeout);
    }
  }

  async function render(html, sectionId, focusTarget) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    if (!parsed?.querySelector('[data-cart-state]')) throw new Error('');
    const cart = state(parsed);
    cart.sections = { [sectionId]: html };
    const current = drawer();
    const body = current.shadowRoot?.querySelector('[part="body"]');
    const scrollTop = body?.scrollTop || 0;
    const scrollLeft = current.querySelector('#recommendations-carousel')?.scrollLeft || 0;
    await new Promise((resolve, reject) => {
      const done = () => {
        clearTimeout(timeout);
        if (body) body.scrollTop = scrollTop;
        const carousel = current.querySelector('#recommendations-carousel');
        if (carousel) carousel.scrollLeft = scrollLeft;
        if (focusTarget) {
          const line = [...current.querySelectorAll('.cart-item')].find(item => item.dataset.variantId === focusTarget.variantId);
          const target = line?.querySelector(focusTarget.selector) || current.querySelector('[data-cart-feedback]');
          if (target) { target.tabIndex = -1; target.focus({ preventScroll: true }); }
        }
        resolve();
      };
      const timeout = setTimeout(() => {
        current.removeEventListener('cart-drawer:refreshed', done);
        reject(new Error(''));
      }, 5000);
      current.addEventListener('cart-drawer:refreshed', done, { once: true });
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'cart:interaction', cart } }));
    });
  }

  async function synchronize(sectionId, focusTarget) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);
    const sections = await request(url.toString());
    if (!sections[sectionId]) throw new Error('');
    await render(sections[sectionId], sectionId, focusTarget);
  }

  async function act(button) {
    if (busy || (needsSync && !button.hasAttribute('data-cart-retry'))) return;
    const current = drawer();
    if (!current) return;
    const external = !button.closest('#cart-drawer');
    if (external) current.show();
    const sectionId = current.dataset.sectionId;
    const line = button.closest('.cart-item');
    const input = line?.querySelector('.cart-item__quantity-input');
    const oldQuantity = input?.value;
    const focusTarget = line ? { variantId: line.dataset.variantId, selector: button.hasAttribute('data-cart-change') ? `[data-cart-change="${button.dataset.cartChange}"]` : '[data-cart-remove-line]' } : null;
    busy = true;
    lock(true);
    button.setAttribute('data-cart-pending', '');
    button.disabled = true;
    line?.classList.add('is-updating');
    feedback(button.hasAttribute('data-cart-add') ? 'adding' : button.hasAttribute('data-cart-remove-line') ? 'removing' : 'updating');
    try {
      if (button.hasAttribute('data-cart-retry')) {
        await synchronize(sectionId, focusTarget);
      } else {
        let path = 'cart/change.js';
        let data;
        if (button.hasAttribute('data-cart-add')) {
          path = 'cart/add.js';
          data = { items: [{ id: Number(button.dataset.cartAdd), quantity: 1 }] };
        } else {
          const quantity = button.hasAttribute('data-cart-remove-line') ? 0 : Math.max(0, Number(oldQuantity) + Number(button.dataset.cartChange));
          if (input) input.value = quantity;
          data = { id: line.dataset.lineKey, quantity };
        }
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.delete('sections');
        sectionUrl.searchParams.delete('section_id');
        data.sections = [sectionId];
        data.sections_url = sectionUrl.pathname + sectionUrl.search;
        const response = await request(root() + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        if (response.sections?.[sectionId]) await render(response.sections[sectionId], sectionId, focusTarget);
        else await synchronize(sectionId, focusTarget);
      }
      needsSync = false;
      feedback('updated');
    } catch (error) {
      if (input) input.value = oldQuantity;
      try {
        await synchronize(sectionId, focusTarget);
        needsSync = false;
        feedback('error', true, false, error.name === 'AbortError' ? '' : error.message);
      } catch {
        needsSync = true;
        feedback('uncertain', true, true);
      }
    } finally {
      busy = false;
      lock(needsSync);
      button.removeAttribute('data-cart-pending');
      if (external) button.disabled = false;
      line?.classList.remove('is-updating');
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-cart-add], [data-cart-change], [data-cart-remove-line], [data-cart-retry]');
    if (!button || !button.closest('#cart-drawer, .complete-the-look-section')) return;
    event.preventDefault();
    act(button);
  });

  document.addEventListener('mousedown', event => {
    const carousel = event.target.closest('#cart-drawer #recommendations-carousel');
    if (!carousel || event.button !== 0 || event.target.closest('button, a, input')) return;
    const startX = event.clientX;
    const startLeft = carousel.scrollLeft;
    const move = moveEvent => { carousel.scrollLeft = startLeft - (moveEvent.clientX - startX); };
    const up = () => { window.removeEventListener('mousemove', move); };
    event.preventDefault();
    window.addEventListener('mousemove', move, { passive: true });
    window.addEventListener('mouseup', up, { once: true });
  });
})();
